/**
 * REST API client for GIERMEK.online backend.
 */

import i18next from 'i18next'
import { config } from '../config'
import { AuthProviders, UserRead, Locale } from '@/types/auth'
import type { SubmitResponsePayload } from '@/types/actions'

// Types
export interface GameConfig {
  min_players: number
  max_players: number
}

export interface CreateSessionRequest {
  game_mode?: string
}

export interface CreateSessionResponse {
  id: string
  session_code: string
  formatted_code: string
  game_mode: string | null
  status: string
  created_at: string
  player_count: number
  game_config: GameConfig | null
}

export interface SessionResponse {
  id: string
  session_code: string
  formatted_code: string
  game_mode: string | null
  status: string
  locale: Locale
  created_at: string
  player_count: number
  game_config: GameConfig | null
}

/** GET /sessions/{code} response: caller may control the lobby (logged in as host). */
export interface SessionDetailResponse extends SessionResponse {
  can_control: boolean
}

export interface JoinSessionRequest {
  name: string
  role?: 'phone' | 'tv'
}

export interface JoinSessionResponse {
  player_id: string
  session_id: string
  name: string
  is_host: boolean
  is_ready: boolean
  is_waiting: boolean
  role: 'phone' | 'tv'
}

export interface GetPlayerStateResponse {
  session_id: string
  session_code: string
  game_mode: string
  phase: string
  players: unknown[]
  current_round: number
  timer_seconds: number | null
  timer_started_at: string | null
  winner: string | null
  pending_question: unknown | null
  narrator_text: string
  display_data: Record<string, unknown>
  version: number
  last_updated: string
  called_numbers: number[]
  bingo_winner: string | null
}

export interface GetTVStateResponse {
  public_state: {
    session_id?: string
    session_code?: string
    phase?: string
    players?: unknown[]
    current_round?: number
    timer_seconds?: number | null
    timer_started_at?: string | null
    winner?: string | null
    [key: string]: unknown
  }
  game_mode_title: string
  version: number
  timestamp: string
}

export interface InsufficientCoinsDetail {
  code: 'INSUFFICIENT_COINS'
  balance: number
  cost: number
  mode_type: string
  message: string
  upgrade_options: Array<{
    code: string
    name: string
    price_pln: number
    coins: number
    days: number
  }>
}

export interface ApiError {
  message: string
  status: number
  detail?: string
  /** Structured detail object for 403 INSUFFICIENT_COINS responses */
  insufficientCoinsDetail?: InsufficientCoinsDetail
}

export function normalizeSessionCode(code: string): string {
  return code.replace(/-/g, '').toUpperCase()
}

// ============================================================================
// AUTH FETCH - cookie-based with silent refresh
// ============================================================================

let refreshPromise: Promise<boolean> | null = null

async function doRefresh(): Promise<void> {
  const res = await fetch(`${config.apiUrl}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
  })
  if (!res.ok) throw new Error(i18next.t('api.refreshFailed'))
}

async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const response = await fetch(url, { ...options, credentials: 'include' })

  if (response.status !== 401) return response

  let succeeded: boolean
  if (refreshPromise) {
    succeeded = await refreshPromise
  } else {
    refreshPromise = doRefresh()
      .then(() => true)
      .catch(() => false)
      .finally(() => {
        refreshPromise = null
      })
    succeeded = await refreshPromise
  }

  if (succeeded) {
    return fetch(url, { ...options, credentials: 'include' })
  }

  // Notify store about lost auth synchronously via dynamic import
  // We can't await here without blocking caller, so we schedule microtask
  // but onAuthLost() is synchronous internally (just set + toast)
  void import('@/stores/authStore').then(({ useAuthStore }) => {
    useAuthStore.getState().onAuthLost()
  })

  return response
}

export async function refreshAuth(): Promise<void> {
  return doRefresh()
}

// ============================================================================
// AUTH API
// ============================================================================

// NOTE: resolved lazily at call time (not a module-level constant) so that
// i18next locale changes are reflected without re-importing this module. See
// .opencode/skills/testing/SKILL.md "Non-React i18n convention".
function getAuthErrorMessages(): Record<string, string> {
  return {
    'Invalid credentials': i18next.t('auth.invalidCredentials'),
    'Invalid email or password': i18next.t('auth.invalidCredentials'),
    'Email already registered': i18next.t('auth.emailAlreadyRegistered'),
    'Username already taken': i18next.t('auth.usernameAlreadyTaken'),
    'Account is inactive': i18next.t('auth.accountInactive'),
    'Too many requests': i18next.t('auth.tooManyRequests'),
  }
}

export async function login(email: string, password: string): Promise<UserRead> {
  let response: Response
  try {
    response = await fetch(`${config.apiUrl}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    })
  } catch {
    throw { message: i18next.t("connection.networkError"), status: 0 } as ApiError
  }
  if (!response.ok) {
    const err = await parseError(response)
    const translated = getAuthErrorMessages()[err.detail || '']
    if (translated) err.message = translated
    throw err
  }
  return response.json()
}

export async function register(
  email: string,
  username: string,
  password: string,
  acceptTerms: boolean
): Promise<UserRead> {
  let response: Response
  try {
    response = await fetch(`${config.apiUrl}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, username, password, accept_terms: acceptTerms }),
    })
  } catch {
    throw { message: i18next.t("connection.networkError"), status: 0 } as ApiError
  }
  if (!response.ok) {
    const err = await parseError(response)
    const translated = getAuthErrorMessages()[err.detail || '']
    if (translated) err.message = translated
    throw err
  }
  return response.json()
}

export async function logout(): Promise<void> {
  try {
    await fetch(`${config.apiUrl}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    })
  } catch {
    // Ignore errors
  }
}

/** Record acceptance of the terms of service for the logged-in user. */
export async function acceptTerms(): Promise<UserRead> {
  let response: Response
  try {
    response = await authFetch(`${config.apiUrl}/auth/accept-terms`, { method: 'POST' })
  } catch {
    throw { message: i18next.t("connection.networkError"), status: 0 } as ApiError
  }
  if (!response.ok) {
    throw await parseError(response)
  }
  return response.json()
}

export async function getMe(): Promise<UserRead> {
  let response: Response
  try {
    response = await authFetch(`${config.apiUrl}/auth/me`)
  } catch {
    throw { message: i18next.t("connection.networkError"), status: 0 } as ApiError
  }
  if (!response.ok) {
    throw await parseError(response)
  }
  return response.json()
}

// ============================================================================
// DEVICE PAIRING - TV login by QR code approved on a logged-in phone
// ============================================================================

export interface DevicePairingStartResponse {
  pairing_code: string
  device_secret: string
  expires_in_seconds: number
}

export interface DevicePairingSessionResponse {
  session_code: string | null
}

async function pairingRequest(url: string, options: RequestInit = {}): Promise<Response> {
  let response: Response
  try {
    response = await authFetch(url, options)
  } catch {
    throw { message: i18next.t("connection.networkError"), status: 0 } as ApiError
  }
  if (!response.ok) {
    throw await parseError(response)
  }
  return response
}

export async function startDevicePairing(): Promise<DevicePairingStartResponse> {
  const response = await pairingRequest(`${config.apiUrl}/auth/device/start`, { method: 'POST' })
  return response.json()
}

export async function approveDevicePairing(pairingCode: string): Promise<void> {
  await pairingRequest(
    `${config.apiUrl}/auth/device/${encodeURIComponent(pairingCode)}/approve`,
    { method: 'POST' },
  )
}

export async function claimDevicePairing(
  pairingCode: string,
  deviceSecret: string,
): Promise<UserRead> {
  const response = await pairingRequest(`${config.apiUrl}/auth/device/claim`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pairing_code: pairingCode, device_secret: deviceSecret }),
  })
  return response.json()
}

export async function notifyDevicePairingSession(
  pairingCode: string,
  sessionCode: string,
): Promise<void> {
  await pairingRequest(
    `${config.apiUrl}/auth/device/${encodeURIComponent(pairingCode)}/session`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_code: sessionCode }),
    },
  )
}

export async function getDevicePairingSession(
  pairingCode: string,
): Promise<DevicePairingSessionResponse> {
  const response = await pairingRequest(
    `${config.apiUrl}/auth/device/${encodeURIComponent(pairingCode)}/session`,
  )
  return response.json()
}

/**
 * Update the current user's locale preference.
 *
 * @param locale - New locale ('pl' | 'en')
 * @returns Updated UserRead
 * @throws ApiError on failure
 */
export async function updateLocale(locale: Locale): Promise<UserRead> {
  let response: Response
  try {
    response = await authFetch(`${config.apiUrl}/auth/me`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ locale }),
    })
  } catch {
    throw { message: i18next.t("connection.networkError"), status: 0 } as ApiError
  }
  if (!response.ok) {
    throw await parseError(response)
  }
  return response.json()
}

/**
 * Fetch which social login providers are configured on the backend.
 *
 * @returns Availability flags per provider
 * @throws ApiError on failure
 */
export async function fetchAuthProviders(): Promise<AuthProviders> {
  let response: Response
  try {
    response = await fetch(`${config.apiUrl}/auth/providers`)
  } catch {
    throw { message: i18next.t('connection.networkError'), status: 0 } as ApiError
  }
  if (!response.ok) {
    throw await parseError(response)
  }
  return response.json()
}

export function startGoogleLogin(): void {
  window.location.href = `${config.apiUrl}/auth/google/login`
}

export function startFacebookLogin(): void {
  window.location.href = `${config.apiUrl}/auth/facebook/login`
}

// NOTE: These are resolved lazily (not module-level constants) so that i18next locale
// changes at runtime are reflected without re-importing this module. See
// .opencode/skills/testing/SKILL.md "Non-React i18n convention" for the rationale.
function getErrorMessages(): Record<number, string> {
  return {
    400: i18next.t("api.error400"),
    403: i18next.t("api.error403"),
    404: i18next.t("join.sessionNotFound"),
    409: i18next.t("api.error409"),
    422: i18next.t("api.error422"),
    429: i18next.t("api.error429"),
    503: i18next.t("api.error503"),
  }
}

function getDetailMessages(): Record<string, string> {
  return {
    // Pydantic validation errors for name field (1-30 chars)
    'Name validation failed': i18next.t("api.nameValidationError"),
    'String should have at least 1 character': i18next.t("api.nameValidationError"),
    'String should have at most 30 characters': i18next.t("api.nameValidationError"),
    // Start game errors (non-dynamic)
    'Only host can start the game': i18next.t("api.onlyHostCanStart"),
    'Game already started': i18next.t("api.gameAlreadyStarted"),
    // Session errors
    'Session is not accepting new players': i18next.t("api.sessionNotAccepting"),
    'Session not found': i18next.t("join.sessionNotFound"),
    'Immediate performance request is required for paid plans': i18next.t(
      "billing.immediatePerformanceRequired"
    ),
    'Cannot start a checkout for a free plan': i18next.t("billing.freePlanNotPurchasable"),
    'Not enough players to start': i18next.t("api.notEnoughPlayers"),
    'Need at least 2 players to start game': i18next.t("api.notEnoughPlayers"),
  }
}

function resolveDetailMessage(detail?: unknown): string | undefined {
  // FastAPI answers a schema violation with an array of error objects, not a string,
  // so the type has to be checked before any string handling.
  if (typeof detail !== 'string' || detail === '') return undefined

  if (detail.startsWith("Player name '") && detail.includes('already taken in this session')) {
    return i18next.t("api.playerNameTaken")
  }

  if (detail.startsWith('Session is full (max ')) {
    const match = detail.match(/Session is full \(max (\d+) players\)/)
    if (match) {
      return i18next.t("api.sessionFull", { count: parseInt(match[1], 10) })
    }
  }

  if (detail.startsWith('Game mode allows at most ')) {
    const match = detail.match(/^Game mode allows at most (\d+)/)
    if (match) {
      return i18next.t("api.modeTooManyPlayers", { count: parseInt(match[1], 10) })
    }
  }

  if (detail.startsWith('Cannot join session - not in LOBBY state')) {
    return i18next.t("api.sessionNotAccepting")
  }

  if (detail.startsWith('Cannot start ')) {
    const match = detail.match(/requires at least (\d+) players, but only (\d+) joined/)
    if (match) {
      return i18next.t("api.notEnoughPlayersMin", { count: parseInt(match[1], 10) })
    }
  }

  if (detail.startsWith('Unknown game mode:')) {
    return i18next.t("game.unknownMode")
  }

  return getDetailMessages()[detail]
}

/**
 * Parse error response and return user-friendly message.
 */
async function parseError(response: Response): Promise<ApiError> {
  let detail: string | undefined
  let insufficientCoinsDetail: InsufficientCoinsDetail | undefined

  try {
    const data = await response.json()
    if (data.detail && typeof data.detail === 'object' && data.detail.code === 'INSUFFICIENT_COINS') {
      insufficientCoinsDetail = data.detail as InsufficientCoinsDetail
      // Use the backend-provided message directly: it carries balance and price
      return {
        message: insufficientCoinsDetail.message,
        status: response.status,
        insufficientCoinsDetail,
      }
    }
    detail = typeof data.detail === 'string' ? data.detail : undefined
  } catch {
    // Response body not JSON
  }

  // Translate detail if known
  const resolvedDetailMessage = resolveDetailMessage(detail)
  const message = resolvedDetailMessage
    ? resolvedDetailMessage
    : getErrorMessages()[response.status] ?? i18next.t("common.error")

  return {
    message,
    status: response.status,
    detail,
  }
}

/**
 * Create a new game session.
 * 
 * @param request - Session creation request with optional game_mode (default: "mafia")
 * @returns Session data with unique code on success
 * @throws ApiError on failure
 */
export async function createSession(
  request: CreateSessionRequest = {}
): Promise<CreateSessionResponse> {
  // Trailing slash required to avoid 307 redirect from FastAPI
  const url = `${config.apiUrl}/sessions/`

  let response: Response

  try {
    response = await authFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    })
  } catch {
    // Network error
    throw {
      message: i18next.t("connection.networkError"),
      status: 0,
    } as ApiError
  }

  if (!response.ok) {
    throw await parseError(response)
  }

  return response.json()
}

/**
 * Join a game session by code.
 * 
 * @param code - Session code (9 chars, dashes optional)
 * @param name - Player display name (1-30 chars)
 * @returns Player data on success
 * @throws ApiError on failure
 */
export async function joinSession(
  code: string,
  name: string,
  role?: 'phone' | 'tv'
): Promise<JoinSessionResponse> {
  // Normalize code (remove dashes, uppercase)
  const normalizedCode = normalizeSessionCode(code)
  
  const url = `${config.apiUrl}/players/join/${normalizedCode}`
  
  let response: Response
  
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name: name.trim(), role }),
    })
  } catch {
    // Network error
    throw {
      message: i18next.t("connection.networkError"),
      status: 0,
    } as ApiError
  }
  
  if (!response.ok) {
    throw await parseError(response)
  }
  
  return response.json()
}

/**
 * Get session info by code.
 * 
 * @param code - Session code (9 chars, dashes optional)
 * @returns Session data including game_config
 * @throws ApiError on failure
 */
export async function getSession(code: string): Promise<SessionDetailResponse> {
  // Normalize code (remove dashes, uppercase)
  const normalizedCode = normalizeSessionCode(code)
  
  const url = `${config.apiUrl}/sessions/${normalizedCode}`
  
  let response: Response
  
  try {
    response = await authFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
  } catch {
    // Network error
    throw {
      message: i18next.t("connection.networkError"),
      status: 0,
    } as ApiError
  }
  
   if (!response.ok) {
      throw await parseError(response)
    }
    
   return response.json()
}

/**
 * Purchase a billing plan via Stripe Checkout.
 *
 * Calls POST /billing/upgrade with plan_code and returns the Stripe checkout URL.
 * Caller should redirect with window.location.href = checkout_url.
 *
 * @param planCode - Plan identifier: "weekend_pass" | "monthly"
 * @returns Object with checkout_url for Stripe redirect
 */
export async function purchasePlan(
  planCode: string,
  requestImmediatePerformance: boolean
): Promise<{ checkout_url: string }> {
  let response: Response

  try {
    response = await authFetch(`${config.apiUrl}/billing/upgrade`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        plan_code: planCode,
        request_immediate_performance: requestImmediatePerformance,
      }),
    })
  } catch {
    throw {
      message: i18next.t("connection.networkError"),
      status: 0,
    } as ApiError
  }

  if (!response.ok) {
    throw await parseError(response)
  }

  return response.json()
}


// ============================================================================
// BILLING API
// ============================================================================

export interface SubscriptionOut {
  id: string
  user_id: string
  plan_id: string
  status: string
  stripe_subscription_id: string | null
  stripe_payment_intent_id: string | null
  started_at: string
  current_period_end: string | null
  canceled_at: string | null
  cancel_at_period_end: boolean
}

export interface PaymentOut {
  id: string
  stripe_invoice_id: string | null
  stripe_payment_intent_id: string | null
  amount_cents: number
  currency: string
  status: string
  created_at: string
}

export interface WalletOut {
  balance: number
  plan_code: string
  period_end: string | null
}

export interface CoinTransactionOut {
  amount: number
  type: 'grant' | 'daily_refill' | 'game_start' | 'forfeit' | 'chargeback'
  mode_type: string | null
  player_count: number | null
  session_id: string | null
  created_at: string
}

export interface PlanOut {
  id: string
  name: string
  code: string
  price_cents: number
  currency: string
  billing_period: string | null
  tier: number
  is_active: boolean
  is_recurring: boolean
  period_days: number
  max_players_per_session: number
  coins_granted: number
}

/**
 * Get current user subscription.
 * Returns null if user has no active subscription.
 */
export async function getSubscription(): Promise<SubscriptionOut | null> {
  let response: Response
  try {
    response = await authFetch(`${config.apiUrl}/billing/subscription`)
  } catch {
    throw { message: i18next.t("connection.networkError"), status: 0 } as ApiError
  }
  if (!response.ok) {
    if (response.status === 404) return null
    throw await parseError(response)
  }
  return response.json()
}

/**
 * Get payment history for current user.
 */
export async function getPayments(): Promise<PaymentOut[]> {
  let response: Response
  try {
    response = await authFetch(`${config.apiUrl}/billing/payments`)
  } catch {
    throw { message: i18next.t("connection.networkError"), status: 0 } as ApiError
  }
  if (!response.ok) {
    throw await parseError(response)
  }
  return response.json()
}

/**
 * Get the current coin balance.
 *
 * This call is what materialises the free tier's daily top-up, so it is issued
 * on entry rather than only when the wallet is displayed.
 */
export async function getWallet(): Promise<WalletOut> {
  let response: Response
  try {
    response = await authFetch(`${config.apiUrl}/billing/wallet`)
  } catch {
    throw { message: i18next.t("connection.networkError"), status: 0 } as ApiError
  }
  if (!response.ok) {
    throw await parseError(response)
  }
  return response.json()
}

/**
 * Get the coin ledger of the current user, newest first.
 */
export async function getWalletHistory(
  limit = 50,
  offset = 0,
): Promise<CoinTransactionOut[]> {
  let response: Response
  try {
    response = await authFetch(
      `${config.apiUrl}/billing/wallet/history?limit=${limit}&offset=${offset}`,
    )
  } catch {
    throw { message: i18next.t("connection.networkError"), status: 0 } as ApiError
  }
  if (!response.ok) {
    throw await parseError(response)
  }
  return response.json()
}

/**
 * Get Stripe Customer Portal URL for managing payments.
 */
export async function getBillingPortalUrl(): Promise<{ portal_url: string }> {
  let response: Response
  try {
    response = await authFetch(`${config.apiUrl}/billing/portal`)
  } catch {
    throw { message: i18next.t("connection.networkError"), status: 0 } as ApiError
  }
  if (!response.ok) {
    throw await parseError(response)
  }
  return response.json()
}

/**
 * Get all available billing plans.
 */
export async function getPlans(): Promise<PlanOut[]> {
  let response: Response
  try {
    response = await authFetch(`${config.apiUrl}/billing/plans`)
  } catch {
    throw { message: i18next.t("connection.networkError"), status: 0 } as ApiError
  }
  if (!response.ok) {
    throw await parseError(response)
  }
  return response.json()
}

// ============================================================================
// HOST MODE API
// ============================================================================

export type HostMode = 'player' | 'display_only'

export interface UpdateHostModeRequest {
  host_mode: HostMode
}

/**
 * Update session host mode.
 *
 * WAŻNE: Wywołaj TYLKO gdy user wybierze "Tylko wyświetlam na TV"
 * Domyślny host_mode to "player", więc dla "Gram na telefonie" nie wywołuj.
 */
export async function updateHostMode(
  code: string,
  hostMode: HostMode
): Promise<SessionResponse> {
  const normalizedCode = normalizeSessionCode(code)
  let response: Response
  try {
    response = await authFetch(`${config.apiUrl}/sessions/${normalizedCode}/host-mode`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ host_mode: hostMode }),
    })
  } catch {
    throw { message: i18next.t("connection.networkError"), status: 0 } as ApiError
  }

  if (!response.ok) {
    throw await parseError(response)
  }

  return response.json()
}

// ============================================================================
// RESPONSE API (Story 5.2 endpoint)
// ============================================================================

export interface ActionResponseResult {
  success: boolean
  request_status: 'pending' | 'completed' | 'timeout'
  responded_count: number
  required_count: number
}

/**
 * Submit player response to a question.
 * 
 * @param sessionCode - Session code (9 chars)
 * @param response - Submit response payload (discriminated union by response_type)
 */
export async function submitResponse(
  sessionCode: string,
  response: SubmitResponsePayload
): Promise<ActionResponseResult> {
  const normalizedCode = normalizeSessionCode(sessionCode)
  const res = await fetch(`${config.apiUrl}/sessions/${normalizedCode}/responses`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(response),
  })

  if (!res.ok) {
    throw await parseError(res)
  }

  return res.json()
}

export interface AISuggestPayload {
  player_id: string
  request_id: string
  field_id: string
}

export interface AISuggestApiResult {
  suggestions: string[]
  error?: string | null
}

/**
 * Request AI-generated suggestions for an ai_text_input form field.
 *
 * Domain rejections (usage limit, wrong phase) come back as HTTP 200 with a
 * populated `error` field — only transport/validation failures throw.
 *
 * @param sessionCode - Session code (9 chars, dashes optional)
 * @param payload - Player, request and field identifiers
 */
export async function submitAISuggest(
  sessionCode: string,
  payload: AISuggestPayload
): Promise<AISuggestApiResult> {
  const normalizedCode = normalizeSessionCode(sessionCode)
  const res = await fetch(`${config.apiUrl}/game/${normalizedCode}/form-ai-suggest`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })

  if (!res.ok) {
    throw await parseError(res)
  }

  return res.json()
}

/**
 * Get list of players in a session.
 * 
 * @param code - Session code (9 chars, dashes optional)
 * @returns List of players in the session
 * @throws ApiError on failure
 */
export async function getSessionPlayers(code: string): Promise<{ players: Array<Record<string, unknown>> }> {
  const normalizedCode = normalizeSessionCode(code)
  
  const url = `${config.apiUrl}/players/session/${normalizedCode}/players`
  
  let response: Response
  
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
  } catch {
    // Network error
    throw {
      message: i18next.t("connection.networkError"),
      status: 0,
    } as ApiError
  }
  
  if (!response.ok) {
    throw await parseError(response)
  }
  
  return response.json()
}

/**
 * Get game mode configuration (min/max players).
 *
 * @param mode - Game mode name (e.g., "bingo", "mafia")
 * @returns Game config for the mode
 * @throws ApiError on failure
 */
export async function getGameModeConfig(mode: string): Promise<GameConfig> {
  const url = `${config.apiUrl}/game/modes/${encodeURIComponent(mode)}/config`

  let response: Response

  try {
    response = await fetch(url)
  } catch {
    throw {
      message: i18next.t("connection.networkError"),
      status: 0,
    } as ApiError
  }

  if (!response.ok) {
    throw await parseError(response)
  }

  return response.json()
}

/**
 * Change game mode in a session (LOBBY only, host only).
 *
 * @param code - Session code
 * @param gameMode - Game mode identifier (e.g., "bingo", "mafia")
 * @returns Updated session lifecycle response
 * @throws ApiError on failure
 */
export async function changeGameMode(code: string, gameMode: string): Promise<SessionResponse> {
  const url = `${config.apiUrl}/sessions/${encodeURIComponent(code)}/game-mode`

  let response: Response

  try {
    response = await authFetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ game_mode: gameMode }),
    })
  } catch {
    throw {
      message: i18next.t("connection.networkError"),
      status: 0,
    } as ApiError
  }

  if (!response.ok) {
    throw await parseError(response)
  }

  return response.json()
}

export interface GameModeListItem {
  id: string
  title: string
  description: string
  min_players: number
  max_players: number
  players: string
  duration: string
  icon: string
  color: string
  free_tier_cutoff: string | null
  coin_cost: number
}

/**
 * Get all available game modes from registry.
 *
 * Sent with credentials so the backend can resolve the caller's access groups;
 * anonymous callers (TV, logged-out) simply carry no session cookie.
 *
 * @param locale - Optional explicit locale override for localized `description`.
 * @returns List of game modes with metadata
 * @throws ApiError on failure
 */
export async function getGameModes(locale?: Locale): Promise<{ modes: GameModeListItem[] }> {
  const url = locale
    ? `${config.apiUrl}/game-modes?locale=${encodeURIComponent(locale)}`
    : `${config.apiUrl}/game-modes`

  let response: Response

  try {
    response = await fetch(url, { credentials: 'include' })
  } catch {
    throw {
      message: i18next.t("connection.networkError"),
      status: 0,
    } as ApiError
  }

  if (!response.ok) {
    throw await parseError(response)
  }

  return response.json()
}

/**
 * Get current game state for a player in a session.
 * 
 * @param sessionId - Session ID (or session code)
 * @param playerId - Player ID
 * @returns Full game state including private_state with phone_info
 * @throws ApiError on failure
 */
export async function getPlayerState(
  sessionId: string,
  playerId: string
): Promise<{ data: GetPlayerStateResponse }> {
  const url = `${config.apiUrl}/sessions/${sessionId}/state?view=phone`
  
  let response: Response
  
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Player-Id': playerId,
      },
    })
  } catch {
    // Network error
    throw {
      message: i18next.t("connection.networkError"),
      status: 0,
    } as ApiError
  }
  
  if (!response.ok) {
    throw await parseError(response)
  }
  
  const data = await response.json()
  return { data }
}

/**
 * Get TV state (public state only, no private data).
 * 
 * @param sessionId - Session ID
 * @param playerId - TV player ID (backend resolves the view from the player role)
 * @returns TV state response with public_state
 * @throws ApiError on failure
 */
export async function getTVState(
  sessionId: string,
  playerId: string
): Promise<{ data: GetTVStateResponse }> {
  const url = `${config.apiUrl}/sessions/${sessionId}/state?view=tv`
  
  let response: Response
  
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Player-Id': playerId,
      },
    })
  } catch {
    // Network error
    throw {
      message: i18next.t("connection.networkError"),
      status: 0,
    } as ApiError
  }
  
  if (!response.ok) {
    throw await parseError(response)
  }
  
  const data = await response.json()
  return { data }
}

/**
 * Submit an action response from a player.
 * 
 * @param sessionId - Session ID
 * @param action - Action object with action_id, player_id, and optional fields
 * @returns Response status
 * @throws ApiError on failure
 */
export async function submitAction(
  sessionId: string,
  action: Record<string, unknown>
): Promise<{ status: string }> {
  const url = `${config.apiUrl}/sessions/${sessionId}/response`

  let response: Response

  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(action),
    })
  } catch {
    // Network error
    throw {
      message: i18next.t("connection.networkError"),
      status: 0,
    } as ApiError
  }

  if (!response.ok) {
    throw await parseError(response)
  }

  return response.json()
}

// ============================================================================
// Game Control Endpoints
// ============================================================================

export interface StartGameResponse extends SessionResponse {
  started_at?: string | null
  host_mode?: string
}

/**
 * Start the game session.
 * Only the host can call this endpoint.
 * 
 * @param sessionCode - Session code (9 chars, dashes optional)
 * @param gameMode - Game mode to start (e.g., "bingo", "mafia")
 * @returns Updated session data
 * @throws ApiError on failure (403 if not host, 400 if already started, 409 if insufficient players)
 */
export async function startGame(
  sessionCode: string,
  gameMode: string
): Promise<StartGameResponse> {
  const normalizedCode = normalizeSessionCode(sessionCode)
  const url = `${config.apiUrl}/sessions/${normalizedCode}/start`

  let response: Response

  try {
    response = await authFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ game_mode: gameMode }),
    })
  } catch {
    // Network error
    throw {
      message: i18next.t("connection.networkError"),
      status: 0,
    } as ApiError
  }

  if (!response.ok) {
    throw await parseError(response)
  }

  return response.json()
}

// ============================================================================
// BINGO Game Endpoints
// ============================================================================

export interface GetBingoCardResponse {
  card: number[][]
  called_numbers: number[]
  last_called_number: number | null
}

/**
 * Leave a game session.
 * 
 * Removes the player from the session and broadcasts player:left event.
 * 
 * @param playerId - Player UUID
 * @returns Void on success
 * @throws ApiError on failure (404 if player not found)
 */
export async function leaveSession(playerId: string): Promise<void> {
   const url = `${config.apiUrl}/players/${encodeURIComponent(playerId)}/leave`

   let response: Response

   try {
     response = await fetch(url, {
       method: 'POST',
       headers: {
         'Content-Type': 'application/json',
       },
     })
   } catch {
     // Network error
     throw {
       message: i18next.t("connection.networkError"),
       status: 0,
     } as ApiError
   }

   if (!response.ok) {
     throw await parseError(response)
   }
}

// ============================================================================
// BINGO Game Endpoints
// ============================================================================

export interface GetBingoCardResponse {
   card: number[][]
   called_numbers: number[]
   last_called_number: number | null
}

/**
 * Get the player's BINGO card.
 * 
 * Fallback for when WebSocket card_generated event is missed due to timing.
 * 
 * @param sessionCode - Session code (9 chars, e.g. "ABC-DEF-GHI")
 * @param playerId - Player UUID
 * @returns Player's BINGO card (5x5 grid) and current game state
 * @throws ApiError on failure (400 if game not started, 403 if player not in session)
 */
export async function getMyBingoCard(
   sessionCode: string,
   playerId: string
): Promise<GetBingoCardResponse> {
   // Normalize code (remove dashes, uppercase)
   const normalizedCode = normalizeSessionCode(sessionCode)
   
   const url = `${config.apiUrl}/sessions/${normalizedCode}/my-card?player_id=${encodeURIComponent(playerId)}`
   
   let response: Response
   
   try {
     response = await fetch(url, {
       method: 'GET',
       headers: {
         'Content-Type': 'application/json',
       },
     })
   } catch {
     // Network error
     throw {
       message: i18next.t("connection.networkError"),
       status: 0,
     } as ApiError
   }
   
   if (!response.ok) {
     throw await parseError(response)
   }
   
   return response.json()
}
